import { useTranslation } from 'react-i18next'
import { Check, X } from 'lucide-react'
import { cn } from '../../../lib/utils'
import {
  KatexText,
  QuizResultFeedback,
  SubmitButton,
  QUIZ_OPTION_STATES,
  QuizImage,
  QuizSolveDescription,
  QuizHeader,
} from '../shared'
import type {
  InlineQuizData,
  MultiChoiceQuizData,
  QuizSolveResult,
} from '../../../types/legacyQuiz'
import { formatText } from '../../../utils/quiz-parser'
import { useInlineQuiz } from '../hooks/use-inline-quiz'

interface InlineAdultProps {
  quizId: string | number
  quiz: InlineQuizData
  onSolve?: (
    quizId: string | number,
    answers: string[][],
    vars: Record<string, unknown>,
    elapsedTime: number,
  ) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[][]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[][]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function InlineAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: InlineAdultProps) {
  const { t } = useTranslation('quiz')

  const {
    children,
    selectedKeysByChild,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolvedCorrectKeySetByChild,
    perChildCorrectsBySelectedKey,
    totalCorrectCount,
    totalChildCount,
    handleSelectChild,
    handleSubmit,
  } = useInlineQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.inline.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        problem={quiz.problem && (
          <div className="text-xl leading-relaxed text-gray-900">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
      />

      {children.map((child, childIdx) => (
        <InlineChildBlock
          key={child.scriptId || childIdx}
          child={child}
          childIdx={childIdx}
          selectedKeys={selectedKeysByChild[childIdx] ?? []}
          isSolved={isSolved}
          isViewSolved={isViewSolved}
          shouldShowCorrectAnswers={shouldShowCorrectAnswers}
          resolvedCorrectKeySet={resolvedCorrectKeySetByChild[childIdx] ?? new Set()}
          correctsBySelectedKey={perChildCorrectsBySelectedKey[childIdx] ?? new Map()}
          onSelect={key => handleSelectChild(childIdx, key)}
          t={t}
        />
      ))}

      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton onClick={handleSubmit} disabled={!canSubmit} label={submitLabel} />
      )}

      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          correctAnswer={
            shouldShowCorrectAnswers
              ? t('types.inline.partialResult', { correct: totalCorrectCount, total: totalChildCount })
              : undefined
          }
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {isSolved && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult?.correct}
          actionLabel={
            isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')
          }
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}

function InlineChildBlock({
  child,
  childIdx,
  selectedKeys,
  isSolved,
  isViewSolved,
  shouldShowCorrectAnswers,
  resolvedCorrectKeySet,
  correctsBySelectedKey,
  onSelect,
  t,
}: {
  child: MultiChoiceQuizData
  childIdx: number
  selectedKeys: string[]
  isSolved: boolean
  isViewSolved: boolean
  shouldShowCorrectAnswers: boolean
  resolvedCorrectKeySet: Set<string>
  correctsBySelectedKey: Map<string, boolean>
  onSelect: (key: string) => void

  t: any
}) {
  const choices =
    child.random && Array.isArray(child.random) && child.random.length > 0
      ? child.random.map(i => child.choices[i]).filter(Boolean)
      : child.choices

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-gray-500">
        {t('types.inline.childLabel', { n: childIdx + 1 })}
      </div>
      {child.problem && (
        <div className="text-lg leading-relaxed text-gray-900">
          <KatexText text={child.problem} />
        </div>
      )}
      <div className="flex flex-wrap gap-3" role="listbox" aria-multiselectable="true">
        {choices.map((choice, i) => {
          const isSelected = selectedKeys.includes(choice.key)
          const isInResolvedCorrect = resolvedCorrectKeySet.has(choice.key)
          const isCorrectByCorrects = correctsBySelectedKey.get(choice.key)
          const showAsCorrect =
            isSolved &&
            (shouldShowCorrectAnswers
              ? isInResolvedCorrect
              : isSelected &&
                (isCorrectByCorrects !== undefined ? isCorrectByCorrects : isInResolvedCorrect))
          const isWrongSelected =
            isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect

          return (
            <button
              key={choice.key}
              role="option"
              aria-selected={isSelected}
              onClick={() => onSelect(choice.key)}
              disabled={isSolved || isViewSolved}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-base font-medium transition-all',
                !isSolved && !isSelected && QUIZ_OPTION_STATES.default,
                !isSolved && !isSelected && 'hover:border-blue-400 hover:bg-blue-50',
                !isSolved && isSelected && QUIZ_OPTION_STATES.selected,
                showAsCorrect && QUIZ_OPTION_STATES.correct,
                isWrongSelected && QUIZ_OPTION_STATES.wrong,
                isSolved && !showAsCorrect && !isWrongSelected && QUIZ_OPTION_STATES.disabled,
                (isSolved || isViewSolved) && 'cursor-not-allowed',
              )}
            >
              <span
                className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0',
                  !isSelected && !isSolved && 'bg-gray-200 text-gray-600',
                  isSelected && !isSolved && 'bg-blue-500 text-white',
                  showAsCorrect && 'bg-green-500 text-white',
                  isWrongSelected && 'bg-red-500 text-white',
                  isSolved && !showAsCorrect && !isWrongSelected && 'bg-gray-100 text-gray-400',
                )}
              >
                {showAsCorrect ? (
                  <Check className="w-3 h-3" />
                ) : isWrongSelected ? (
                  <X className="w-3 h-3" />
                ) : (
                  i + 1
                )}
              </span>
              {choice.image && (
                <QuizImage
                  image={choice.image}
                  alt={t('types.multiChoice.optionAlt', { number: i + 1 })}
                  className="my-0 shrink-0"
                />
              )}
              {choice.answer && <KatexText text={choice.answer} />}
            </button>
          )
        })}
      </div>
    </div>
  )
}
